import React from 'react';
import { AppProvider, useApp } from './context/AppContext.tsx';
import TopBar from './components/layout/TopBar.tsx';
import Navbar from './components/layout/Navbar.tsx';
import Footer from './components/layout/Footer.tsx';
import FloatingContactWidget from './components/layout/FloatingContactWidget.tsx';
import Home from './pages/Home.tsx';
import BlogCatalog from './pages/BlogCatalog.tsx';
import ProductsCatalog from './pages/ProductsCatalog.tsx';
import PortfolioCatalog from './pages/PortfolioCatalog.tsx';
import PortfolioDetailPage from './components/portfolio/PortfolioDetailPage.tsx';
import GalleryCatalog from './pages/GalleryCatalog.tsx';
import MediaShowcaseCatalog from './pages/MediaShowcaseCatalog.tsx';
import ArticleDetail from './components/article/ArticleDetail.tsx';
import ProductDetailPage from './components/product/ProductDetailPage.tsx';
import AboutPage from './components/static/AboutPage.tsx';
import ContactPage from './components/static/ContactPage.tsx';
import CustomPageView from './components/static/CustomPageView.tsx';
import LoginPage from './components/auth/LoginPage.tsx';
import AdminDashboard from './components/admin/AdminDashboard.tsx';
import ProductDetailModal from './components/product/ProductDetailModal.tsx';
import PortfolioDetailModal from './components/portfolio/PortfolioDetailModal.tsx';
import AuthorModal from './components/article/AuthorModal.tsx';
import SEOAndIntegrations from './components/common/SEOAndIntegrations.tsx';
import ToastNotification from './components/common/ToastNotification.tsx';

function AppContent() {
  const {
    currentPage,
    currentArticleSlug,
    currentProductSlug,
    currentPortfolioSlug,
    settings,
    isLoading,
    toast,
    hideToast,
    user,
  } = useApp();

  if (isLoading && !settings) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
        <div className="relative">
          <div className="w-10 h-10 border-3 border-emerald-600 border-t-transparent rounded-full animate-spin" />
        </div>
        <h2 className="text-slate-700 font-bold text-xs mt-4">Memuat data website...</h2>
      </div>
    );
  }

  // If on admin route and logged in, render AdminDashboard
  if (currentPage === 'admin') {
    if (!user) {
      const secret = (settings?.security?.customLoginPath || '').replace(/^\/+|\/+$/g, '').toLowerCase().trim();
      if (secret) {
        return <Home />;
      }
      return <LoginPage />;
    }
    return <AdminDashboard />;
  }

  // If on login route: if secret URL is configured, make sure the visitor accessed via the secret path
  if (currentPage === 'login') {
    const secret = (settings?.security?.customLoginPath || '').replace(/^\/+|\/+$/g, '').toLowerCase().trim();
    if (secret && !user) {
      const currentPath = (typeof window !== 'undefined' ? window.location.pathname : '').replace(/^\/+|\/+$/g, '').toLowerCase().trim();
      if (currentPath !== secret) {
        return <Home />;
      }
    }
  }

  const isHome = currentPage === 'home';
  const bgColor = isHome
    ? settings?.theme?.homepageBgColor || '#F8FAFC'
    : settings?.theme?.pageBgColor || '#FFFFFF';

  return (
    <div
      id="app-root-wrapper"
      className="min-h-screen flex flex-col transition-colors duration-300 antialiased"
      style={{ backgroundColor: bgColor }}
    >
      {/* Top Banner (Contact & Quick Promo) */}
      <TopBar />

      {/* Navigation Bar */}
      <Navbar />

      {/* Main Page Content */}
      <main className="flex-1">
        {currentPage === 'home' && <Home />}
        {currentPage === 'blog' && <BlogCatalog />}
        {currentPage === 'article-detail' && <ArticleDetail slugOrId={currentArticleSlug || ''} />}
        {currentPage === 'products' && <ProductsCatalog />}
        {currentPage === 'product-detail' && <ProductDetailPage slugOrId={currentProductSlug || ''} />}
        {currentPage === 'portfolio' && <PortfolioCatalog />}
        {currentPage === 'portfolio-detail' && <PortfolioDetailPage slugOrId={currentPortfolioSlug || ''} />}
        {currentPage === 'gallery' && <GalleryCatalog />}
        {currentPage === 'media-showcase' && <MediaShowcaseCatalog />}
        {currentPage === 'about' && <AboutPage />}
        {currentPage === 'contact' && <ContactPage />}
        {currentPage === 'custom-page' && <CustomPageView />}
        {currentPage === 'login' && <LoginPage />}
      </main>

      {/* Footer */}
      <Footer />

      {/* Global Interactive Modals */}
      <SEOAndIntegrations />
      <ProductDetailModal />
      <PortfolioDetailModal />
      <AuthorModal />

      {/* Floating Eye-Catching WhatsApp & Phone Contact Buttons */}
      <FloatingContactWidget />

      {/* Global Toast Notification */}
      <ToastNotification toast={toast} onClose={hideToast} />
    </div>
  );
}

export default function App() {
  return (
    <AppProvider>
      <AppContent />
    </AppProvider>
  );
}
